import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchTournament,
    fetchParticipants,
    fetchLeagueConfig,
    fetchLeagueView,
    saveLeagueConfig,
    saveLeagueGroups,
    assignPlayersToGroups,
    startLeague,
    advanceLeagueRound,
    changeLeagueRoundEndDate,
    changeLeagueRoundStartDate,
    addLeaguePlayer,
    modifyLeagueGroups,
    addLeaguePlayerToGroupWithMatches,
    removeLeaguePlayerFromGroup,
    registerTournamentParticipantBySupervisor,
    unRegisterTournamentParticipant,
    rollbackLeagueRound,
    rollbackLeagueStart,
    adminFreezePlayer,
    adminUnfreezePlayer,
    sendLeagueActivityReminders,
    runProposalDigest,
    drawRandomPairs
} from '../../api/api';
import CustomModal from '../../UI/Modal/CustomModal';
import Spinner from '../Spinner/Spinner';
import styles from './AdminLeagueManagement.module.css';

const AdminLeagueManagement = () => {
    const { tournamentId } = useParams();
    const navigate = useNavigate();
    const [tournament, setTournament] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [config, setConfig] = useState({
        scoringSystem: 'CLASSIC',
        promotedPerGroup: 1,
        relegatedPerGroup: 1,
        daysPerRound: 14,
        leagueEndDate: '',
        maxFreezes: 2,
        frozenCompensationPct: 0.7,
        placeBonusTop3Step: 0.05,
        countdownEnabled: true,
        matchPlanningEnabled: true,
    });
    const [groups, setGroups] = useState([]);
    const [groupAssignments, setGroupAssignments] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [modalMessage, setModalMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEndDate, setNewEndDate] = useState('');
    const [newStartDate, setNewStartDate] = useState('');
    const [addPlayerEmail, setAddPlayerEmail] = useState('');
    const [addPartnerEmail, setAddPartnerEmail] = useState('');
    const [leagueMembers, setLeagueMembers] = useState([]);
    const [isPositiveModal, setIsPositiveModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmLabel, setConfirmLabel] = useState('');
    const [seededIds, setSeededIds] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [leagueViewData, setLeagueViewData] = useState(null);

    useEffect(() => {
        loadData();
    }, [tournamentId]);

    const loadData = async () => {
        setIsLoading(true);
        const tournamentResult = await fetchTournament(tournamentId);
        if (tournamentResult.success) {
            setTournament(tournamentResult.data);
        }

        const participantsResult = await fetchParticipants(tournamentId);
        if (participantsResult.success) {
            setParticipants(participantsResult.data);
        }

        const configResult = await fetchLeagueConfig(tournamentId);
        if (configResult.success && configResult.data) {
            setConfig(configResult.data);
            if (configResult.data.currentRoundEndDate) {
                setNewEndDate(configResult.data.currentRoundEndDate.substring(0, 16));
            }
            if (configResult.data.currentRoundStartDate) {
                setNewStartDate(configResult.data.currentRoundStartDate.substring(0, 16));
            }
        }

        // Load existing league view (groups + assignments) if league is active
        const leagueViewResult = await fetchLeagueView(tournamentId, null);
        if (leagueViewResult.success && leagueViewResult.data) {
            const viewData = leagueViewResult.data;
            setLeagueViewData(viewData);
            if (viewData.groupStandings && viewData.groupStandings.length > 0) {
                const loadedGroups = viewData.groupStandings.map((gs, i) => ({
                    groupLabel: gs.groupLabel,
                    weight: gs.weight,
                    groupPremium: gs.groupPremium ?? 0,
                    displayOrder: i,
                }));
                setGroups(loadedGroups);

                // Build assignments from standings (current players in each group)
                const loadedAssignments = {};
                viewData.groupStandings.forEach(gs => {
                    loadedAssignments[gs.groupLabel] = gs.standings.map(s => s.participantId);
                });
                setGroupAssignments(loadedAssignments);
            }
        }

        setIsLoading(false);
    };

    const extractErrorMessage = (data) => {
        if (data == null) return 'Nieznany błąd';
        if (typeof data === 'string') return data;
        if (typeof data === 'object') return data.message || data.error || JSON.stringify(data);
        return String(data);
    };

    const handleSaveConfig = async () => {
        const result = await saveLeagueConfig(tournamentId, config);
        if (result.success) {
            setConfig(result.data);
            showModal('Konfiguracja zapisana', true);
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleAddGroup = () => {
        const label = String.fromCharCode(65 + groups.length); // A, B, C...
        // Default premium scale dobrana do nowej skali matchPoints (średnia 0–3.3):
        // A=5, B=4, C=3, D=2, E=1, F+=0. Liniowy gap 1, ratio matchPoints/premium ~66%.
        const defaultPremiums = [5, 4, 3, 2, 1];
        const defaultPremium = defaultPremiums[groups.length] ?? 0;
        setGroups([...groups, {
            groupLabel: label,
            weight: 1.0 - groups.length * 0.2,
            groupPremium: defaultPremium,
            members: []
        }]);
        setGroupAssignments({ ...groupAssignments, [label]: [] });
    };

    const handleRemoveGroup = (index) => {
        const removed = groups[index];
        const newGroups = groups.filter((_, i) => i !== index);
        const newAssignments = { ...groupAssignments };
        delete newAssignments[removed.groupLabel];
        setGroups(newGroups);
        setGroupAssignments(newAssignments);
    };

    const handleWeightChange = (index, weight) => {
        const newGroups = [...groups];
        newGroups[index].weight = parseFloat(weight) || 0;
        setGroups(newGroups);
    };

    const handleGroupPremiumChange = (index, value) => {
        const newGroups = [...groups];
        newGroups[index].groupPremium = parseFloat(value) || 0;
        setGroups(newGroups);
    };

    const handleAssignPlayer = (groupLabel, participantId) => {
        // Remove from any other group
        const newAssignments = {};
        for (const [label, ids] of Object.entries(groupAssignments)) {
            newAssignments[label] = ids.filter(id => id !== participantId);
        }
        newAssignments[groupLabel] = [...(newAssignments[groupLabel] || []), participantId];
        setGroupAssignments(newAssignments);
    };

    const handleRemovePlayer = (groupLabel, participantId) => {
        const newAssignments = { ...groupAssignments };
        newAssignments[groupLabel] = newAssignments[groupLabel].filter(id => id !== participantId);
        setGroupAssignments(newAssignments);
    };

    const handleSaveGroupsAndAssignments = async () => {
        // 1. Zapisz strukturę grup (etykiety + wagi + premia + bonus miejsca)
        const groupDtos = groups.map((g, i) => ({
            groupLabel: g.groupLabel,
            weight: g.weight,
            groupPremium: g.groupPremium ?? 0,
            displayOrder: i,
        }));
        const groupResult = await saveLeagueGroups(tournamentId, groupDtos);
        if (!groupResult.success) {
            showModal('Błąd zapisu grup: ' + groupResult.data);
            return;
        }

        // 2. Jeśli liga aktywna — użyj modifyGroups (kasuje mecze + przypisuje + regeneruje)
        const roundNumber = config.currentRoundNumber || 1;

        if (isLeagueStarted) {
            // Sprawdź czy w bieżącej rundzie są wyniki
            const viewResult = await fetchLeagueView(tournamentId, roundNumber);
            if (viewResult.success && viewResult.data) {
                const hasResults = viewResult.data.groupStandings.some(gs =>
                    gs.matches.some(m => m.participantWinnerId !== null)
                );
                if (hasResults) {
                    showModal('Nie można zmienić składu grup — w bieżącej rundzie są już wpisane wyniki.');
                    return;
                }
            }

            const modifyResult = await modifyLeagueGroups(tournamentId, roundNumber, groupAssignments);
            if (modifyResult.success) {
                showModal('Grupy zapisane, mecze wygenerowane ponownie', true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(modifyResult.data));
            }
        } else {
            // Liga jeszcze nie wystartowała — tylko przypisz
            const assignResult = await assignPlayersToGroups(tournamentId, roundNumber, groupAssignments);
            if (assignResult.success) {
                showModal('Grupy i przypisania graczy zapisane', true);
            } else {
                showModal('Błąd przypisania graczy: ' + assignResult.data);
            }
        }
    };

    const handleStartLeague = async () => {
        const result = await startLeague(tournamentId);
        if (result.success) {
            showModal('Liga wystartowana!', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleAdvanceRound = () => {
        setConfirmAction(() => async () => {
            const result = await advanceLeagueRound(tournamentId);
            if (result.success) {
                showModal('Przejście do nowej rundy', true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(result.data));
            }
        });
        setConfirmLabel('Tak, przejdź');
        setModalMessage('Czy na pewno chcesz przejść do następnej rundy? Zostaną wykonane awanse i spadki graczy.');
        setIsPositiveModal(false);
        setIsModalOpen(true);
    };

    const handleRollbackLeagueStart = () => {
        setConfirmAction(() => async () => {
            const result = await rollbackLeagueStart(tournamentId);
            if (result.success) {
                showModal('Start ligi cofnięty. Turniej wrócił do stanu planowanego — możesz ponownie zdefiniować grupy.', true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(result.data));
            }
        });
        setConfirmLabel('Tak, cofnij start');
        setModalMessage(
            'Cofnięcie startu ligi usunie wszystkie wygenerowane mecze rundy 1, przypisania do grup i nieopłacone wpisowe. ' +
            'Pary deblowe i konfiguracja grup pozostaną. Kontynuować?'
        );
        setIsPositiveModal(false);
        setIsModalOpen(true);
    };

    const handleRollbackRound = () => {
        if (config.currentRoundNumber <= 1) {
            showModal('Nie można cofnąć — to jest pierwsza runda. Jeśli chcesz wrócić przed start ligi, użyj przycisku „Cofnij start ligi".');
            return;
        }
        setConfirmAction(() => async () => {
            const result = await rollbackLeagueRound(tournamentId);
            if (result.success) {
                showModal('Cofnięto do rundy ' + (config.currentRoundNumber - 1), true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(result.data));
            }
        });
        setConfirmLabel('Tak, cofnij');
        setModalMessage(`Czy na pewno chcesz usunąć rundę ${config.currentRoundNumber} i cofnąć się do rundy ${config.currentRoundNumber - 1}? Wszystkie mecze bieżącej rundy zostaną usunięte.`);
        setIsPositiveModal(false);
        setIsModalOpen(true);
    };

    const handleConfirm = async () => {
        setIsModalOpen(false);
        if (confirmAction) {
            await confirmAction();
            setConfirmAction(null);
            setConfirmLabel('');
        }
    };

    const handleChangeStartDate = async () => {
        if (!newStartDate) return;
        const result = await changeLeagueRoundStartDate(tournamentId, newStartDate);
        if (result.success) {
            showModal('Data rozpoczęcia rundy zmieniona', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleChangeEndDate = async () => {
        if (!newEndDate) return;
        const result = await changeLeagueRoundEndDate(tournamentId, newEndDate);
        if (result.success) {
            showModal('Data zakończenia rundy zmieniona', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleRemoveParticipant = (participantId) => {
        const name = getParticipantName(participantId);
        setModalMessage(`Czy na pewno chcesz usunąć „${name}" z turnieju?`);
        setConfirmLabel('Tak, usuwam');
        setIsPositiveModal(false);
        setConfirmAction(() => async () => {
            const result = await unRegisterTournamentParticipant(participantId);
            if (result.success) {
                showModal(`Usunięto „${name}" z turnieju.`, true);
                loadData();
            } else {
                showModal(extractErrorMessage(result.data) || 'Błąd podczas usuwania.');
            }
        });
        setIsModalOpen(true);
    };

    const handleAddPlayerByEmail = async () => {
        if (!addPlayerEmail) {
            showModal('Podaj email gracza');
            return;
        }

        // Sprawdź czy gracz już jest w jakiejś grupie
        const allAssignedIds = new Set(Object.values(groupAssignments).flat());
        const existingParticipant = participants.find(
            p => (p.email?.toLowerCase() === addPlayerEmail.toLowerCase() ||
                  p.username?.toLowerCase() === addPlayerEmail.toLowerCase())
        );
        if (existingParticipant && allAssignedIds.has(existingParticipant.id)) {
            const groupLabel = Object.entries(groupAssignments).find(
                ([, ids]) => ids.includes(existingParticipant.id)
            )?.[0];
            showModal(`Gracz ${addPlayerEmail} jest już przypisany do grupy ${groupLabel || '?'}`);
            return;
        }

        // Zarejestruj gracza (z partnerem deblowym jeśli podany) w turnieju
        const partner = (addPartnerEmail || '').trim();
        const registerResult = await registerTournamentParticipantBySupervisor(tournament.id, addPlayerEmail, partner);
        if (!registerResult.success) {
            showModal('Błąd rejestracji: ' + (registerResult.data || 'Nieznany błąd'));
            return;
        }

        const pairInfo = partner ? ` z partnerem ${partner}` : '';
        showModal('Gracz ' + addPlayerEmail + pairInfo + ' dodany do turnieju. Przypisz go do grupy w sekcji Grupy.', true);
        setAddPlayerEmail('');
        setAddPartnerEmail('');
        loadData();
    };

    const handleModifyGroups = async () => {
        const roundNumber = config.currentRoundNumber || 1;

        // Sprawdź czy w bieżącej rundzie są wpisane wyniki
        const viewResult = await fetchLeagueView(tournamentId, roundNumber);
        if (viewResult.success && viewResult.data) {
            const hasResults = viewResult.data.groupStandings.some(gs =>
                gs.matches.some(m => m.participantWinnerId !== null)
            );
            if (hasResults) {
                showModal('Nie można przegenerować meczów — w bieżącej rundzie są już wpisane wyniki. Usuń wyniki przed modyfikacją grup.');
                return;
            }
        }

        const result = await modifyLeagueGroups(tournamentId, roundNumber, groupAssignments);
        if (result.success) {
            showModal('Skład grup zmieniony, mecze wygenerowane ponownie', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleSendReminders = async () => {
        const result = await sendLeagueActivityReminders(tournamentId);
        if (result.success) {
            showModal(`Wysłano ${result.data.sent} przypomnień`, true);
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleRunProposalDigest = async () => {
        const result = await runProposalDigest(tournamentId);
        if (result.success) {
            showModal(`Wysłano ${result.data.sent} maili z nowymi ofertami`, true);
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleAdminFreeze = async (participantId) => {
        const result = await adminFreezePlayer(tournamentId, participantId);
        if (result.success) {
            showModal('Zamrożenie zgłoszone', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const handleAdminUnfreeze = async (participantId) => {
        const result = await adminUnfreezePlayer(tournamentId, participantId);
        if (result.success) {
            showModal('Odmrożenie wykonane', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    const showModal = (msg, positive = false) => {
        setModalMessage(msg);
        setIsPositiveModal(positive);
        setConfirmAction(null);
        setConfirmLabel('');
        setIsModalOpen(true);
    };

    const getUnassignedParticipants = () => {
        const assignedIds = new Set(Object.values(groupAssignments).flat());
        return participants.filter(p => !assignedIds.has(p.id));
    };

    const getParticipantName = (participantId) => {
        const p = participants.find(pp => pp.id === participantId);
        if (!p) return `ID: ${participantId}`;
        const main = `${p.firstName} ${p.lastName}`;
        if (p.doublesPartnerFirstName && p.doublesPartnerLastName) {
            return `${main} / ${p.doublesPartnerFirstName} ${p.doublesPartnerLastName}`;
        }
        return main;
    };

    const renderParticipantPair = (p) => {
        if (!p) return null;
        const main = `${p.firstName} ${p.lastName}`;
        const partner = p.doublesPartnerFirstName && p.doublesPartnerLastName
            ? `${p.doublesPartnerFirstName} ${p.doublesPartnerLastName}` : null;
        return (
            <>
                <div>{main}</div>
                {partner && <div>{partner}</div>}
            </>
        );
    };

    const toggleSeeded = (participantId) => {
        setSeededIds(prev => prev.includes(participantId)
            ? prev.filter(id => id !== participantId)
            : [...prev, participantId]);
    };

    const computeHasPlayedInGroup = (participantId, groupLabel) => {
        const gs = leagueViewData?.groupStandings?.find(g => g.groupLabel === groupLabel);
        return gs?.matches?.some(m =>
            (m.participant1Id === participantId || m.participant2Id === participantId)
            && m.participantWinnerId !== null
        ) || false;
    };

    const handleAddPlayerToGroupRunning = (groupLabel, participantId) => {
        const name = getParticipantName(participantId);
        const groupSize = (groupAssignments[groupLabel] || []).length;
        setModalMessage(
            `Dodać „${name}" do grupy ${groupLabel}? ` +
            `Zostanie wygenerowanych ${groupSize} nowych meczów (gracz vs każdy z obecnych członków). ` +
            `Istniejące mecze i wyniki w grupie nie zostaną zmienione. ` +
            `Uwaga: gracz dołącza w trakcie rundy — może mieć punkty karne za poprzednie tygodnie nieaktywności, dopóki nie nadrobi.`
        );
        setConfirmLabel('Tak, dodaj i wygeneruj mecze');
        setIsPositiveModal(false);
        setConfirmAction(() => async () => {
            const result = await addLeaguePlayerToGroupWithMatches(tournamentId, participantId, groupLabel);
            if (result.success) {
                showModal(`Dodano „${name}" do grupy ${groupLabel}.`, true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(result.data));
            }
        });
        setIsModalOpen(true);
    };

    const handleRemovePlayerFromGroupRunning = (groupLabel, participantId) => {
        const name = getParticipantName(participantId);
        setModalMessage(
            `Usunąć „${name}" z grupy ${groupLabel}? ` +
            `Wszystkie mecze tego gracza w bieżącej rundzie zostaną usunięte (wraz z propozycjami terminów). ` +
            `Pozostałe mecze grupy między innymi graczami nie zostaną zmienione.`
        );
        setConfirmLabel('Tak, usuń z grupy');
        setIsPositiveModal(false);
        setConfirmAction(() => async () => {
            const result = await removeLeaguePlayerFromGroup(tournamentId, participantId);
            if (result.success) {
                showModal(`Usunięto „${name}" z grupy ${groupLabel}.`, true);
                loadData();
            } else {
                showModal('Błąd: ' + extractErrorMessage(result.data));
            }
        });
        setIsModalOpen(true);
    };

    const handleDrawRandomPairs = async () => {
        setIsDrawing(true);
        const result = await drawRandomPairs(tournamentId, seededIds);
        setIsDrawing(false);
        if (result.success) {
            setSeededIds([]);
            showModal('Pary wylosowane', true);
            loadData();
        } else {
            showModal('Błąd: ' + extractErrorMessage(result.data));
        }
    };

    if (isLoading) return <Spinner />;

    const isLeagueStarted = tournament && tournament.status === 'ACTIVE';

    const formatDateTime = (dt) => {
        if (!dt) return '-';
        return new Date(dt).toLocaleString('pl-PL', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className={styles.container}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
                ← Powrót
            </button>
            <h2 className={styles.title}>
                Zarządzanie Ligą: {tournament?.title || ''}
            </h2>

            {isLeagueStarted && config.currentRoundNumber > 0 && (
                <section className={styles.section}>
                    <h3>Status ligi</h3>
                    <div className={styles.statusGrid}>
                        <div><strong>Aktualna runda:</strong> {config.currentRoundNumber}</div>
                        <div><strong>Początek rundy:</strong> {formatDateTime(config.currentRoundStartDate)}</div>
                        <div><strong>Koniec rundy:</strong> {formatDateTime(config.currentRoundEndDate)}</div>
                        <div><strong>Początek ligi:</strong> {formatDateTime(tournament?.tournamentDate)}</div>
                        <div><strong>Koniec ligi:</strong> {config.leagueEndDate ? formatDateTime(config.leagueEndDate) : 'Nie ustawiono'}</div>
                    </div>
                </section>
            )}

            {/* LOSOWANIE PAR DEBLOWYCH */}
            {tournament?.randomDoublesDraw === true && !isLeagueStarted && (() => {
                const active = participants.filter(p => !p.isReserve);
                const activeCount = active.length;
                const maxSeeded = Math.floor(activeCount / 2);
                const isDoubles = tournament?.type === 'DOUBLES';
                const canDraw = !isDoubles && activeCount > 0 && activeCount % 2 === 0 && seededIds.length <= maxSeeded;

                return (
                    <section className={styles.section}>
                        <h3>Losowanie par deblowych</h3>
                        {!isDoubles ? (
                            <>
                                <p className={styles.infoText}>
                                    Ten turniej zaczyna w trybie singlowym. Oznacz graczy rozstawionych
                                    (max {maxSeeded} z {activeCount}) — <strong>dwóch rozstawionych nie może trafić do jednej pary</strong>.
                                    Następnie kliknij <strong>Losuj pary</strong>.
                                </p>
                                {activeCount % 2 !== 0 && (
                                    <p className={styles.warningText}>
                                        ⚠ Liczba graczy ({activeCount}) jest nieparzysta. Oznacz jednego gracza jako rezerwę przed losowaniem.
                                    </p>
                                )}
                                <div className={styles.seededCounter}>
                                    Rozstawieni: <strong>{seededIds.length}</strong> / max {maxSeeded}
                                </div>
                                <ul className={styles.seededList}>
                                    {active.map(p => (
                                        <li key={p.id} className={styles.seededRow}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={seededIds.includes(p.id)}
                                                    onChange={() => toggleSeeded(p.id)}
                                                    disabled={!seededIds.includes(p.id) && seededIds.length >= maxSeeded}
                                                />
                                                {p.firstName} {p.lastName}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className={styles.saveButton}
                                    onClick={handleDrawRandomPairs}
                                    disabled={!canDraw || isDrawing}
                                >
                                    {isDrawing ? 'Losowanie...' : '🎲 Losuj pary'}
                                </button>
                            </>
                        ) : (
                            <>
                                <p className={styles.infoText}>
                                    Pary wylosowane. Możesz wylosować ponownie (kasuje obecne pary) dopóki liga nie wystartuje.
                                </p>
                                <ul className={styles.pairsList}>
                                    {participants.filter(p => p.doublesPartnerId).map(p => (
                                        <li key={p.id}>
                                            {p.firstName} {p.lastName}
                                            {' + '}
                                            {p.doublesPartnerFirstName} {p.doublesPartnerLastName}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className={styles.saveButton}
                                    onClick={handleDrawRandomPairs}
                                    disabled={isDrawing}
                                >
                                    {isDrawing ? 'Losowanie...' : '🎲 Losuj ponownie'}
                                </button>
                            </>
                        )}
                    </section>
                );
            })()}

            {/* KONFIGURACJA */}
            <section className={styles.section}>
                <h3>Konfiguracja Ligi</h3>
                <div className={styles.formGrid}>
                    <label>
                        System punktacji:
                        <select value={config.scoringSystem} onChange={e => setConfig({ ...config, scoringSystem: e.target.value })}>
                            <option value="CLASSIC">Klasyczny (2:0)</option>
                            <option value="SYSTEM_3210">System 3-2-1-0</option>
                        </select>
                    </label>
                    <label>
                        Awansuje:
                        <input type="number" min="0" max="3" value={config.promotedPerGroup}
                               onChange={e => setConfig({ ...config, promotedPerGroup: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label>
                        Spada:
                        <input type="number" min="0" max="3" value={config.relegatedPerGroup}
                               onChange={e => setConfig({ ...config, relegatedPerGroup: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label>
                        Dni na rundę:
                        <input type="number" min="1" max="60" value={config.daysPerRound}
                               onChange={e => setConfig({ ...config, daysPerRound: parseInt(e.target.value) || 7 })} />
                    </label>
                    <label>
                        Maks. zamrożeń na sezon:
                        <input type="number" min="0" max="10" value={config.maxFreezes}
                               onChange={e => setConfig({ ...config, maxFreezes: parseInt(e.target.value) || 0 })} />
                    </label>
                    <label title="Procent średniej rundy gracza przyznawany jako kompensacja za każdą zamrożoną rundę (0 = bez kompensacji, 100 = pełna kompensacja, 70 = rekomendowane)">
                        Kompensacja zamrożenia (%):
                        <input type="number" min="0" max="100" step="5"
                               value={Math.round((config.frozenCompensationPct ?? 0) * 100)}
                               onChange={e => setConfig({ ...config, frozenCompensationPct: (parseInt(e.target.value) || 0) / 100 })} />
                    </label>
                    <label title="Krok % premii za top-3 miejsca w grupach: 1m = 3×step, 2m = 2×step, 3m = 1×step, 4m+ = 0. Remis → wyższa wartość. 0 wyłącza bonus. Parametr wspólny dla wszystkich grup.">
                        Bonus top-3 (%):
                        <input type="number" min="0" max="50" step="1"
                               value={Math.round((config.placeBonusTop3Step ?? 0) * 100)}
                               onChange={e => setConfig({ ...config, placeBonusTop3Step: (parseInt(e.target.value) || 0) / 100 })} />
                    </label>
                    <label title="Gdy odznaczone — licznik czasu rundy jest ukryty i nie wymusza automatycznego przejścia do następnej rundy.">
                        Countdown:
                        <input type="checkbox"
                               checked={config.countdownEnabled ?? true}
                               onChange={e => setConfig({ ...config, countdownEnabled: e.target.checked })} />
                    </label>
                    <label title="Gdy odznaczone — przycisk 'Planowanie meczów' w widoku ligi jest ukryty dla uczestników.">
                        Planowanie meczów:
                        <input type="checkbox"
                               checked={config.matchPlanningEnabled ?? true}
                               onChange={e => setConfig({ ...config, matchPlanningEnabled: e.target.checked })} />
                    </label>
                    <label>
                        Data zakończenia ligi:
                        <input type="datetime-local" value={config.leagueEndDate || ''}
                               onChange={e => setConfig({ ...config, leagueEndDate: e.target.value })} />
                    </label>
                </div>
                <button className={styles.saveButton} onClick={handleSaveConfig}>Zapisz konfigurację</button>
            </section>

            {/* GRUPY */}
            <section className={styles.section}>
                <h3>Grupy i punktacja</h3>
                {groups.map((group, index) => (
                    <div key={group.groupLabel} className={styles.groupBlock}>
                        <div className={styles.groupHeader}>
                            <strong>Grupa {group.groupLabel}</strong>
                            <label title="Stała baza punktowa za udział w grupie, proporcjonalna do rozegranych/zaplanowanych meczów">
                                Premia grupowa:
                                <input type="number" step="0.25" min="0" value={group.groupPremium ?? 0}
                                       onChange={e => handleGroupPremiumChange(index, e.target.value)}
                                       className={styles.weightInput} />
                            </label>
                            <button className={styles.removeButton} onClick={() => handleRemoveGroup(index)}>Usuń</button>
                        </div>
                        <div className={styles.groupPlayers}>
                            {(groupAssignments[group.groupLabel] || []).map(pid => {
                                const hasPlayed = isLeagueStarted && computeHasPlayedInGroup(pid, group.groupLabel);
                                const onChipRemove = isLeagueStarted
                                    ? () => handleRemovePlayerFromGroupRunning(group.groupLabel, pid)
                                    : () => handleRemovePlayer(group.groupLabel, pid);
                                const title = isLeagueStarted
                                    ? (hasPlayed
                                        ? 'Gracz ma rozegrane mecze — nie można usunąć'
                                        : 'Usuń gracza z grupy w bieżącej rundzie')
                                    : 'Usuń z grupy';
                                return (
                                    <div key={pid} className={styles.playerChip}>
                                        {getParticipantName(pid)}
                                        <button
                                            onClick={onChipRemove}
                                            disabled={hasPlayed}
                                            title={title}
                                        >×</button>
                                    </div>
                                );
                            })}
                            <select onChange={e => {
                                if (!e.target.value) return;
                                const pid = parseInt(e.target.value);
                                e.target.value = '';
                                if (isLeagueStarted) {
                                    handleAddPlayerToGroupRunning(group.groupLabel, pid);
                                } else {
                                    handleAssignPlayer(group.groupLabel, pid);
                                }
                            }}>
                                <option value="">+ Dodaj gracza</option>
                                {getUnassignedParticipants()
                                    .filter(p => !p.isReserve)
                                    .filter(p => !isLeagueStarted || !p.frozenForNextRound)
                                    .map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.firstName} {p.lastName}{p.doublesPartnerFirstName && p.doublesPartnerLastName ? ` / ${p.doublesPartnerFirstName} ${p.doublesPartnerLastName}` : ''}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                ))}

                {getUnassignedParticipants().length > 0 && (
                    <div className={styles.unassignedBlock}>
                        <h4 className={styles.unassignedTitle}>
                            Uczestnicy bez grupy <span className={styles.unassignedHint}>(możesz usunąć z turnieju)</span>
                        </h4>
                        <div className={styles.unassignedList}>
                            {getUnassignedParticipants().map(p => (
                                <div key={p.id} className={styles.unassignedChip}>
                                    <span className={styles.unassignedName}>{getParticipantName(p.id)}</span>
                                    <button
                                        type="button"
                                        className={styles.removeFromTournamentBtn}
                                        onClick={() => handleRemoveParticipant(p.id)}
                                        title="Usuń z turnieju"
                                        aria-label={`Usuń ${getParticipantName(p.id)} z turnieju`}
                                    >
                                        🗑
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!isLeagueStarted && (
                    <button className={styles.addGroupButton} onClick={handleAddGroup}>+ Dodaj grupę</button>
                )}
                {!isLeagueStarted && (
                    <div className={styles.buttonRow}>
                        <button className={styles.saveButton} onClick={handleSaveGroupsAndAssignments}>Zapisz grupy i przypisania</button>
                    </div>
                )}
                {isLeagueStarted && (
                    <p className={styles.infoText} style={{marginTop: '12px'}}>
                        Liga aktywna — zmiany w składzie grup zapisują się natychmiast po wybraniu „+ Dodaj gracza" lub kliknięciu „×" przy graczu.
                    </p>
                )}
            </section>

            {/* AKCJE */}
            <section className={styles.section}>
                <h3>Akcje</h3>
                <div className={styles.actionsGrid}>
                    {!isLeagueStarted && (
                        <button className={styles.startButton} onClick={handleStartLeague}>Wystartuj ligę</button>
                    )}
                    {isLeagueStarted && (
                        <>
                            <button className={styles.actionButton} onClick={handleAdvanceRound}>Następna runda</button>
                            {config.currentRoundNumber === 1 ? (
                                <button className={styles.dangerButton} onClick={handleRollbackLeagueStart}>
                                    Cofnij start ligi
                                </button>
                            ) : (
                                <button className={styles.dangerButton} onClick={handleRollbackRound}>Cofnij rundę</button>
                            )}
                            <div className={styles.actionRow}>
                                <input type="datetime-local" value={newStartDate}
                                       onChange={e => setNewStartDate(e.target.value)} />
                                <button className={styles.actionButton} onClick={handleChangeStartDate}>
                                    Zmień datę początku rundy
                                </button>
                            </div>
                            <div className={styles.actionRow}>
                                <input type="datetime-local" value={newEndDate}
                                       onChange={e => setNewEndDate(e.target.value)} />
                                <button className={styles.actionButton} onClick={handleChangeEndDate}>
                                    Zmień datę końca rundy
                                </button>
                            </div>
                        </>
                    )}
                    <div className={styles.actionRow}>
                        <input
                            type="email"
                            placeholder="Email gracza"
                            value={addPlayerEmail}
                            onChange={e => setAddPlayerEmail(e.target.value)}
                            className={styles.emailInput}
                        />
                        <input
                            type="email"
                            placeholder="Email partnera deblowego (opcjonalnie)"
                            value={addPartnerEmail}
                            onChange={e => setAddPartnerEmail(e.target.value)}
                            className={styles.emailInput}
                        />
                        <button className={styles.actionButton} onClick={handleAddPlayerByEmail}>
                            {tournament?.type === 'DOUBLES' ? 'Dodaj parę do turnieju' : 'Dodaj gracza do turnieju'}
                        </button>
                    </div>
                    {isLeagueStarted && config.matchPlanningEnabled && (
                        <>
                            <button className={styles.actionButton} onClick={handleSendReminders}>Generuj przypomnienia</button>
                            <button className={styles.actionButton} onClick={handleRunProposalDigest}>Wyślij digest ofert</button>
                        </>
                    )}
                </div>
            </section>

            {/* ZAMROŻENIA */}
            {isLeagueStarted && config.matchPlanningEnabled && (
                <section className={styles.section}>
                    <h3>Zamrożenia graczy</h3>
                    <p className={styles.freezeInfo}>
                        Limit zamrożeń na sezon: <strong>{config.maxFreezes}</strong>
                    </p>
                    <table className={styles.freezeTable}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Gracz</th>
                                <th>Wykorzystane</th>
                                <th>Status</th>
                                <th>Akcja</th>
                                <th>Piłki</th>
                            </tr>
                        </thead>
                        <tbody>
                            {participants.map((p, index) => (
                                <tr key={p.id}>
                                    <td>{index + 1}</td>
                                    <td>{renderParticipantPair(p)}</td>
                                    <td>{p.freezesUsed || 0} / {config.maxFreezes}</td>
                                    <td>
                                        {p.frozenForNextRound
                                            ? <span className={styles.frozenBadge}>Zamrożony (nast. runda)</span>
                                            : <span className={styles.activeBadge}>Aktywny</span>
                                        }
                                    </td>
                                    <td>
                                        {p.frozenForNextRound ? (
                                            <button className={styles.unfreezeSmallButton} onClick={() => handleAdminUnfreeze(p.id)}>
                                                Odmroź
                                            </button>
                                        ) : (
                                            <button className={styles.freezeSmallButton} onClick={() => handleAdminFreeze(p.id)}>
                                                Zamroź
                                            </button>
                                        )}
                                    </td>
                                    <td>
                                        {(() => {
                                            const provided = p.ballsProvided || 0;
                                            const played = p.matchesPlayedTotal || 0;
                                            const pct = played > 0 ? Math.round((provided / played) * 100) : 0;
                                            return `${provided}/${played}\u00A0\u00A0\u00A0${pct}%`;
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={() => { setIsModalOpen(false); setConfirmAction(null); setConfirmLabel(''); }}
                message={modalMessage}
                isPositiveMessage={isPositiveModal}
                cancelLabel="Zamknij"
                cancelButtonColor={isPositiveModal ? 'green' : undefined}
                showConfirmButton={!!confirmAction}
                confirmLabel={confirmLabel}
                onConfirm={handleConfirm}
                confirmButtonColor="red"
            />
        </div>
    );
};

export default AdminLeagueManagement;
